import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  PAGE_LOGIN_KEY,
  SILENT_LOGIN_KEY,
  attemptedRecently,
  forgetAttempt,
  rememberAttempt,
  userSwitchKey,
} from '@/features/session/attemptGuard';
import {
  LOGOUT_URL,
  WINDOW_LOGIN_URL,
  currentPath,
  loginUrl,
  takeLoginRequired,
} from '@/features/session/loginUrls';
import { listenSignedIn } from '@/features/session/signedInSignal';
import {
  SessionContextValue,
  SessionMode,
  SessionState,
  SessionUser,
  UserResponse,
} from '@/features/session/types';
import http from '@/utils/http';

const LOGIN_WINDOW_NAME = 'gasbi-ai-login';
const LOGIN_WINDOW_FEATURES = 'width=520,height=640';

const toSessionUser = (response: UserResponse): SessionUser => ({
  sub: response.claims.find(claim => claim.type === 'sub')?.value ?? '',
  name: response.name ?? '',
});

const statusOf = (error: unknown) =>
  (error as { response?: { status?: number } } | null)?.response?.status;

// The states that wait for a sign-in elsewhere: only these re-check on the signal from /auth/done.
const isAwaitingSignIn = (state: SessionState) =>
  state.status === 'loginRequired' ||
  state.status === 'waitingForWindow' ||
  state.status === 'userMismatch';

/** The session state machine behind SessionProvider (ADR-0004 §2.3–2.6); see SessionState for the states. */
export const useSessionMachine = (mode: SessionMode): SessionContextValue => {
  const [state, setState] = useState<SessionState>({ status: 'checking' });
  // The current state without waiting for a render: ensureUser and the sign-in signal decide on it.
  const stateRef = useRef(state);
  const expectedSubRef = useRef<string | null>(null);

  const transition = useCallback((next: SessionState) => {
    stateRef.current = next;
    setState(next);
  }, []);

  const navigate = useCallback(
    (url: string) => {
      transition({ status: 'signingIn' });
      window.location.assign(url);
    },
    [transition],
  );

  // §2.5: a session of another user than the host app's switches by a silent login, which replaces the cookie;
  // once per expected user and minute, then the user signs in by hand.
  const settle = useCallback(
    (user: SessionUser) => {
      const expectedSub = expectedSubRef.current;
      if (!expectedSub || expectedSub === user.sub) {
        if (expectedSub) forgetAttempt(userSwitchKey(expectedSub));
        transition({ status: 'signedIn', user });
        return;
      }
      if (attemptedRecently(userSwitchKey(expectedSub))) {
        transition({ status: 'userMismatch', user });
        return;
      }
      rememberAttempt(userSwitchKey(expectedSub));
      navigate(loginUrl(currentPath(), { silent: true }));
    },
    [navigate, transition],
  );

  // A 401 on its own: the panel tries the silent login, the full page the login page (§2.3, §2.4), once a minute.
  const signInAutomatically = useCallback(() => {
    const key = mode === 'embed' ? SILENT_LOGIN_KEY : PAGE_LOGIN_KEY;
    if (attemptedRecently(key)) {
      transition({ status: 'loginRequired', popupBlocked: false });
      return;
    }
    rememberAttempt(key);
    navigate(loginUrl(currentPath(), { silent: mode === 'embed' }));
  }, [mode, navigate, transition]);

  const check = useCallback(
    async (automaticSignIn: boolean) => {
      transition({ status: 'checking' });
      let user: SessionUser;
      try {
        const response = await http.get<UserResponse>('/user');
        user = toSessionUser(response.data);
      } catch (error) {
        const status = statusOf(error);
        if (status === 401 && automaticSignIn) {
          signInAutomatically();
        } else if (status === 401) {
          transition({ status: 'loginRequired', popupBlocked: false });
        } else if (status === 403) {
          transition({ status: 'noAccess' });
        } else {
          transition({ status: 'error' });
        }
        return;
      }
      forgetAttempt(SILENT_LOGIN_KEY);
      forgetAttempt(PAGE_LOGIN_KEY);
      settle(user);
    },
    [settle, signInAutomatically, transition],
  );

  // On load: login=required is the end of a failed silent login, no second automatic attempt then.
  useEffect(() => {
    void check(!takeLoginRequired());
  }, [check]);

  useEffect(
    () =>
      listenSignedIn(() => {
        if (isAwaitingSignIn(stateRef.current)) void check(false);
      }),
    [check],
  );

  const openWindow = useCallback(() => {
    const opened = window.open(
      WINDOW_LOGIN_URL,
      LOGIN_WINDOW_NAME,
      LOGIN_WINDOW_FEATURES,
    );
    transition(
      opened
        ? { status: 'waitingForWindow' }
        : { status: 'loginRequired', popupBlocked: true },
    );
  }, [transition]);

  const signIn = useCallback(() => {
    if (mode === 'embed') openWindow();
    else navigate(loginUrl(currentPath()));
  }, [mode, navigate, openWindow]);

  const retry = useCallback(() => void check(true), [check]);

  const ensureUser = useCallback(
    (expectedSub: string) => {
      expectedSubRef.current = expectedSub;
      // Any other state settles later, when /agents/user answers.
      const current = stateRef.current;
      if (current.status === 'signedIn' || current.status === 'userMismatch')
        settle(current.user);
    },
    [settle],
  );

  // §2.6: no sign-out in the panel — ending the Identity Server session there would sign the user out of the host app.
  const signOut = useCallback(async () => {
    if (mode !== 'page') return;
    try {
      await http.delete('/auth');
    } catch {
      // The logout below ends the session anyway.
    }
    window.location.assign(LOGOUT_URL);
  }, [mode]);

  return useMemo(
    () => ({
      mode,
      state,
      signIn,
      reopenWindow: openWindow,
      retry,
      ensureUser,
      signOut,
    }),
    [mode, state, signIn, openWindow, retry, ensureUser, signOut],
  );
};
